// blankBlueprintGraph — the empty-but-VALID graph for each surface.
//
// This is what "new" means for a blueprint. It exists as its own module for two
// reasons: it is DATA rather than loader logic, and two callers need it —
// writeEmptyBlueprintsForCharacter (localStorage, for a brand-new character)
// and the content browser's New → Blueprint → <graph> (a server row). One
// definition, so a new character and a new card cannot disagree about what a
// blank graph is.
//
// It also replaces a worse answer. New used to COPY whatever the editor was
// holding for that surface, so "New → Blueprint → Body graph" produced a clone
// of the character's current body graph under a name that said otherwise. The
// justification was ENG-991 — an EMPTY graph breaks the character if anyone
// makes it the default — which argues for never shipping an empty graph, not
// for copying. Duplicate already exists for copying, in the blueprint
// inspector, and it copies from the SERVER row rather than the editor's cache:
// the correct source for "copy this card", and another sign New should not have
// been doing it.

import { CAMERA_RIG_TEMPLATES, instantiateRigAsGroup } from './cameraRigTemplates.js'

/**
 * A fresh blank graph per surface: `{ body, face, events }`.
 *
 * Two details here are load-bearing rather than cosmetic:
 *
 *   `id: 'final'` — BlueprintEditor recomputes the output node's `connected`
 *   flag by matching that id (`n.id === 'final'`), so an output node named
 *   anything else renders as permanently unwired no matter what you connect to
 *   it, which reads as the graph being broken.
 *
 *   The blank EVENT graph carries the orbit camera rig rather than a bare entry
 *   node. A blueprint with no camera nodes falls back to the editor's
 *   OrbitControls and wears a "no rig" chip; a new graph that immediately
 *   announces something missing is not a blank, it is a broken one.
 *
 * Fresh objects on every call: the two callers write into different places, and
 * the body and face output nodes are the same shape, so sharing one object
 * between them would be easy to do by accident and would let an edit to one
 * graph reach into the other.
 */
export function blankBlueprintGraphs() {
  const finalPose = () => ({
    id: 'final',
    type: 'finalPose',
    position: { x: 500, y: 200 },
    // false, not true: nothing IS wired yet. The shipped conversational.json
    // carries `connected: true` only because its output really is connected.
    data: { connected: false },
    deletable: false,
  })
  let n = 0
  const nextId = () => `ev_${++n}`
  const orbitRig = instantiateRigAsGroup(CAMERA_RIG_TEMPLATES.orbit, { x: 80, y: 80 }, nextId)
  return {
    body:   { nodes: [finalPose()], edges: [] },
    face:   { nodes: [finalPose()], edges: [] },
    events: { eventNodes: [orbitRig], eventEdges: [], variables: [], functions: {} },
  }
}
